import {
  type IAdminCategory,
  type ILanguageType,
  type ITranslation,
  type ITranslationArray,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Col, Divider, Form, Input, Row, Select } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { seoLive, seoUpdate } from '../../reducers/category/reducer';
import { SPACING } from '../../theme/tokens';
import FormSubmitFooter from '../form-submit-footer';

interface IForm {
  description: string;
  metaDescription: string;
  metaKeywords: string[];
}

function getKeywords(
  metaKeywords: ITranslationArray[] | undefined,
  code: string | undefined,
): string[] {
  if (!metaKeywords || !code) {
    return [];
  }
  return metaKeywords.find((t) => t.code === code)?.value ?? [];
}

const getDirectTranslation = (
  translations: ITranslation[] | undefined,
  lang?: ILanguageType,
): string | null =>
  translations?.find(({ code }: ITranslation): boolean => code === lang?.code)
    ?.value ?? null;

function SeoForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const category = useSelector((s: RootState) => s.Category.category);
  const categoryInProcess = useSelector((s: RootState) => s.Category.inProcess);
  const categoryUpdatedAt = useSelector((s: RootState) => s.Category.updatedAt);

  const [form] = Form.useForm<IForm>();

  const initialValues: IForm = useMemo(
    () => ({
      description:
        getDirectTranslation(category?.descriptions, languageType) ?? '',
      metaDescription:
        getDirectTranslation(category?.metaDescriptions, languageType) ?? '',
      metaKeywords: getKeywords(category?.metaKeywords, languageType?.code),
    }),
    [
      category?.descriptions,
      category?.metaDescriptions,
      category?.metaKeywords,
      languageType,
      categoryUpdatedAt,
    ],
  );

  useEffect((): void => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const onFinish = useCallback(
    (values: IForm, cat: IAdminCategory | undefined): void => {
      if (cat && languageType) {
        dispatch(
          seoUpdate(
            cat.uuid,
            languageType.code,
            values.description,
            values.metaDescription,
            values.metaKeywords,
          ),
        );
      }
    },
    [languageType?.code, dispatch],
  );

  const dispatchLive = useCallback((): void => {
    if (!languageType) {
      return;
    }

    const values = form.getFieldsValue();
    dispatch(
      seoLive(
        languageType.code,
        values.description,
        values.metaDescription,
        values.metaKeywords,
      ),
    );
  }, [languageType?.code, dispatch, form]);

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={(values: IForm): void | null | undefined =>
        category && onFinish(values, category)
      }
      layout="vertical"
    >
      <Row>
        <Col span={3}>
          <FormSubmitFooter
            inProcess={categoryInProcess}
            updatedAt={categoryUpdatedAt}
          />
        </Col>
      </Row>

      <Divider />

      <Row gutter={SPACING.lg}>
        <Col span={24}>
          <Form.Item name="description" label="Description">
            <Input.TextArea
              placeholder="Category description"
              disabled={categoryInProcess}
              rows={3}
              onBlur={dispatchLive}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name="metaDescription"
            label="Meta description"
            rules={[{ type: 'string', max: 160 }]}
          >
            <Input.TextArea
              placeholder="~155 characters for search engine snippet"
              disabled={categoryInProcess}
              rows={2}
              onBlur={dispatchLive}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item name="metaKeywords" label="Meta keywords">
            <Select
              mode="tags"
              disabled={categoryInProcess}
              placeholder="Type keyword and press Enter"
              onChange={dispatchLive}
              tokenSeparators={[',']}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

function Seo(): React.ReactElement {
  return <SeoForm />;
}

export default Seo;
